import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Compass } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    document.title = "Payment Status — TheKhoj";
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.payment_status === "expired" || data.status === "failed") {
          setState("failed");
          return;
        }
      } catch {
        // keep polling
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-card border border-border rounded-3xl p-10 text-center shadow-xl"
      >
        <span className="w-14 h-14 rounded-full overflow-hidden ring-1 ring-border inline-block mb-6">
          <img src="/logos/compass_mark.png" alt="TheKhoj" className="w-full h-full object-cover" />
        </span>
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">Please hold on for a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold" data-testid="payment-success-heading">Enrolment Confirmed!</h1>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Welcome to TheKhoj family. We will reach out within 48 hours to schedule the Orientation Session
              and share the welcome kit details.
            </p>
            <a
              href="https://wa.me/919035083242?text=Hi%20TheKhoj!%20I%20just%20completed%20the%20enrolment%20payment."
              target="_blank" rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold text-sm"
              data-testid="payment-success-whatsapp"
            >
              Say Hello on WhatsApp
            </a>
          </>
        )}
        {(state === "failed" || state === "error") && (
          <>
            <h1 className="font-display text-2xl font-bold">Payment not confirmed</h1>
            <p className="text-sm text-muted-foreground mt-3">
              If money was debited, don't worry — ping us on WhatsApp +91 9035083242 and we'll confirm manually.
            </p>
            <Link to="/" className="mt-6 inline-block text-sm font-semibold text-primary" data-testid="payment-back-home">
              ← Back to home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
